import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Table, Card, TableColumn, Bubble } from 'tea-component';
import { getName, selectValByCondition } from '@home/utils/common';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import Abnormal from '@home/components/Abnormal';
import { BaseServiceChildrenProps } from '@/pages/benchmark/types/commom';
import { history } from 'umi';
import { FRAME_TYPE_MAP_REVERSE } from '@/pages/benchmark/_config/constant';
import { metricValueFormatter } from '@/pages/benchmark/utils/utils';
import '@/pages/benchmark/assets/scss/page/genre/components/MetricTable.scss';

const { scrollable } = Table.addons;
export interface IMetricTableColumn {
  key: string;
  headerEn: string;
  headerCn: string;
}
export interface IMetricTableProps extends BaseServiceChildrenProps {
  // column配置
  columns: IMetricTableColumn[];
  // 第一列名称
  firstColumnNameList: {
    nameEn: string;
    nameCn: string;
    value: string;
  }[];
  // 进入指标详情提示文案
  goDetaiTextEn?: string;
  goDetailTextCn?: string;
  [key: string]: any;
}

const MetricTable = (props: IMetricTableProps) => {
  const {
    columns = [],
    firstColumnNameList = [],
    dataKey = 'data',
    data,
    filterVal,
    currentType,
    openMoreFilter,
    goDetaiTextEn = 'View metric details',
    goDetailTextCn = '进入指标详情页',
  } = props;

  const { loading, errorMsg, data: _records } = data[dataKey] || {};
  const metricMap = data.metricConfig || {};

  const records = _records || [];

  const { isEN } = useLocal();

  const tableRef = useRef<HTMLDivElement>(null);
  // 列宽度
  const [columnWidth, setColumnWidth] = useState<number | string>('auto');
  // 表格宽度
  const [tableWidth, setTableWidth] = useState<number>();

  // 一页只展示5个
  const setColWidth = useCallback(() => {
    if (columns.length > 5) {
      const tableWidth = tableRef.current?.clientWidth || 0;
      const colWidth = (tableWidth - 82) / 5;
      setTableWidth(tableWidth);
      setColumnWidth(colWidth);
    } else {
      setColumnWidth('auto');
      setTableWidth(undefined);
    }
  }, [columns]);

  useEffect(() => {
    setColWidth();
  }, [columns, records, isEN]);

  useEffect(() => {
    window.removeEventListener('resize', setColWidth);
    window.addEventListener('resize', setColWidth);
    return () => {
      window.removeEventListener('resize', setColWidth);
    };
  }, [columns]);

  // 跳转详情页
  const goMetricDetail = (key: string) => {
    // 通过url记录当前查询条件
    const { filters, metrics, frame_type } = filterVal;
    const {
      // game_list = [],
      country_list = [],
      genre_list = [],
      platform_list = [],
    } = filters || {};
    const newParams = new URLSearchParams(window.location.search);
    newParams.set('type', currentType);
    newParams.set('genre', String(genre_list[0] || ''));
    newParams.set('platform', String(platform_list[0]));
    newParams.set('country', String(country_list[0]));
    newParams.set('metrics', String(metrics));
    newParams.set('openMoreFilter', String(openMoreFilter));
    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
    window.history.replaceState({ path: newUrl }, '', newUrl);
    // 跳转详情, 区分otdr指标和常规指标
    const { value_type = '' } = metricMap[key] || {};
    let linkPage;
    if (value_type.trim() === 'otdr') {
      linkPage = 'metricOtdr';
    } else if (value_type.trim() === 'otdr,tips') {
      linkPage = 'metricOtdr';
    } else if (value_type.trim() === 'tips') {
      linkPage = 'metricTips';
    } else {
      linkPage = 'metricDetail';
    }
    // 参数编码
    const detailParams = new URLSearchParams({});
    detailParams.set('genre_subgenre', String(genre_list[0] || ''));
    detailParams.set('platform', String(platform_list[0]));
    detailParams.set('country', String(country_list[0]));
    const link = `/benchmark/${
      FRAME_TYPE_MAP_REVERSE[frame_type]
    }/explore/${linkPage}/${key}?${detailParams.toString()}`;
    history.push(link);
  };

  // 组装column配置
  const newColumns = useMemo(() => {
    const columnList: TableColumn[] = [
      {
        key: '_index',
        header: '',
        align: 'left',
        width: 82,
        fixed: 'left',
      },
      ...(columns?.map((item) => {
        const { key, headerCn, headerEn } = item || {};
        const { formatter, scalar_type } = metricMap[key] || {};
        const col: TableColumn = {
          key,
          header: (
            <Bubble content={getName(isEN, goDetaiTextEn, goDetailTextCn)}>
              <span
                className="metric-title"
                onClick={() => goMetricDetail(key)}
              >
                {selectValByCondition(isEN, headerEn, headerCn)}
              </span>
            </Bubble>
          ),
          align: 'center',
          width: columnWidth,
          render: (row) =>
            metricValueFormatter(row[key], { formatter, scalar_type }),
        };
        return col;
      }) || []),
    ];

    return columnList;
  }, [columns, isEN, columnWidth]);

  // 组装数据
  const newRecord = useMemo(() => {
    const list = firstColumnNameList?.map((item) => {
      const { nameCn, nameEn } = item;
      // 设置首列名称
      const obj: any = { _index: selectValByCondition(isEN, nameEn, nameCn) };
      // 根据firstColumnNameList配置组装数据
      for (const dataItem of records) {
        obj[dataItem?.key] = dataItem[item?.value];
      }
      return obj;
    });
    return list;
  }, [firstColumnNameList, isEN, records]);

  return (
    <Card className="dbb-benchmark-card dbb-tablecomm-card">
      <Card.Body>
        <Abnormal
          isLoading={loading}
          isError={!!errorMsg}
          style={{ height: 251 }}
          component={
            <Table
              ref={tableRef}
              className="dbb-tablecomm"
              records={newRecord}
              columns={newColumns}
              addons={[
                scrollable({
                  // 超过5个保证最低宽度
                  minWidth: tableWidth,
                }),
              ]}
            />
          }
        />
      </Card.Body>
    </Card>
  );
};
export default MetricTable;
